import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { AnimationController } from 'providers/animation-controller';
import { SettingsService } from 'services/settings.service';
import { formatLocaleFloat } from '@cf-shared/shared-util';
import { LoggingService } from 'services/logging.service';
import { describeError } from '@cf-shared/error-describe';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { TelemetryService } from 'services/telemetry.service';
import { CameraInitResult } from 'services/camera.service';
import { LabelScannerPageParams } from './label-scanner-interface';
import { EditFoodPageParams, IEditFoodPage, setPendingLabelScanImage } from 'pages/edit-food/edit-food-interface';
import { ScanLabelResults, ScanLabelModelResult, ScanLabelError, ScanQuotaRejection, hasPartialNutrients } from '@cf-shared/scan-label-interface';
import { SCAN_MODELS } from '@cf-shared/scan-models';
import { AlertController } from 'providers/alert-controller';
import { ScanErrorAlertKind, runScanFailureRecoveryAsync } from 'services/scan-error-policy';
import { showScanRetryAlertAsync } from 'services/scan-error-alert';
import { WakeLockService } from 'services/wake-lock.service';
import { TranslatedString } from 'models/translated-string';
import { nutrientName } from 'util/formatting';
import { Nutrients } from '@cf-shared/data-model/food';
import { ConfigDbService } from 'services/config-db.service';
import { ScanMode } from 'services/scan-config';
import { ScannerModelSelection } from 'services/scanner-model-selection';
import { FakeScanProgress } from 'services/scan-progress';
import { MultiModelFallbackManager, runMultiModelScanAsync } from 'services/multi-model-fallback';
import { PopoverController } from 'providers/popover-controller';
import { openScannerImagePicker } from 'services/scanner-file-picker';
import {
  CameraStopOrdering,
  ScannerPageAnimationHooks,
  animateCapturedPhotoToReelAsync,
  animateCollageTransitionAsync,
} from 'services/scanner-capture-animations';
import { ZoomImagePopover } from 'components/zoom-image-popover/zoom-image-popover';
import { StepConfig, StepState, StepperStyle } from 'components/cs-stepper/cs-stepper';
import { CsCameraViewComponent } from 'components/cs-camera-view/cs-camera-view';
import { CsScannerCameraStageComponent } from 'components/cs-scanner-camera-stage/cs-scanner-camera-stage';
import { CsScannerPhotoReelComponent } from 'components/cs-scanner-photo-reel/cs-scanner-photo-reel';
import { OnlineStateService } from 'services/online-state.service';
import { ResultGroup, groupResultsBySimilarity, nutrientAgreements } from './label-scan-result-groups';
import { labelScanCropPixels } from './label-scan-crop';
import { LabelScanOutcome, LabelScanRunTracker, labelScanFailureReport } from './label-scan-failure';
import { wasReportedByCloudFunctions } from 'services/cloud-functions-errors';
import { ScannerName } from 'services/camera-permission-help';

/**
 * The parts of the food editor's `local` block that come from reading the LABEL.
 *
 * Deliberately not the whole block: `scannedBarcode` and `returnContext` are carried through
 * from whoever opened this scanner and are set by `replaceWithEditFoodAsync` itself, after the
 * readings are spread in. Typed this way, a caller that tried to supply either is refused at
 * compile time rather than having its value silently overwritten.
 */
type LabelReadings = Omit<NonNullable<EditFoodPageParams['local']>, 'scannedBarcode' | 'returnContext'>;

@Component({
  selector: 'page-label-scanner',
  templateUrl: 'label-scanner-page.html',
  host: { 'class': 'label-scanner cs-scanner-page' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LabelScannerPage extends CsPage<LabelScannerPageParams> {
  protected ScannerName = ScannerName;
  protected LL = LL;
  protected StepperStyle = StepperStyle;

  @ViewChild('stage') stage: CsScannerCameraStageComponent;
  @ViewChild('reel') reel: CsScannerPhotoReelComponent;

  /** the wrapped camera view inside the shared camera stage */
  get cameraView(): CsCameraViewComponent | undefined {
    return this.stage?.cameraView;
  }

  cameraPermissionDismissed = false;
  capturedPhotos: { dataUrl: string; base64: string }[] = [];
  analyzing = false;
  /** Set while an error alert is showing — hides the Done button to prevent re-analysis */
  errorAlertOpen = false;
  readonly MAX_PHOTOS = 3;
  // factories, not resolved strings — keeps the hints language-reactive
  readonly photoHints = [() => LL.labelScanner.photoHintExtract(), () => LL.labelScanner.photoHintImage()];
  private pageEnteredAt = 0;

  // shared fake-progress driver with the label scanner's cycling status lines
  private fakeProgress = new FakeScanProgress(
    [
      () => LL.labelScanner.statusReadingPackaging(),
      () => LL.labelScanner.statusExtractingProductInfo(),
      () => LL.labelScanner.statusAnalyzingNutrients(),
      () => LL.labelScanner.statusCheckingIngredients(),
      () => LL.labelScanner.statusDetectingServingSizes(),
      () => LL.labelScanner.statusAlmostThere(),
    ],
    () => this.onProgressTick(),
  );

  get analyzeProgress(): number { return this.fakeProgress.progress; }
  get analyzeStatusText(): string { return this.fakeProgress.statusText; }

  // model selection — shared with the other scanners. Non-private: the
  // template reads it to render the shared model picker.
  readonly modelSelection = new ScannerModelSelection();

  retryMessage: string = null;
  private multiModelFallback = new MultiModelFallbackManager();

  // what the shared capture animations need from this page. A getter, not a
  // field: the injected animationController is not assigned yet while field
  // initialisers run.
  private get animationHooks(): ScannerPageAnimationHooks {
    return {
      animationController: this.animationController,
      cameraElement: () => this.cameraView?.getCameraElement() ?? null,
      reelSlotElements: () => this.reel?.slotElements ?? [],
      collagePhotoElements: () => this.stage?.collagePhotoElements ?? [],
      capturedPhotos: () => this.capturedPhotos,
      isViewDestroyed: () => this.viewDestroyed,
      markForCheck: () => this.cd.markForCheck(),
    };
  }

  // comparison results (for 'all' mode)
  resultGroups: ResultGroup[] = null;
  activeGroupIndex = 0;
  private scanId: string = null;

  protected settings: SettingsService;
  private logging: LoggingService;

  constructor(
    settings: SettingsService,
    logging: LoggingService,
    private navCtrl: NavController,
    private cloudFunctions: CloudFunctionsService,
    private configDb: ConfigDbService,
    private telemetry: TelemetryService,
    private animationController: AnimationController,
    private alertCtrl: AlertController,
    private popoverCtrl: PopoverController,
    private cd: ChangeDetectorRef,
    private wakeLock: WakeLockService,
    private onlineState: OnlineStateService,
  ) {
    super();
    this.settings = settings;
    this.logging = logging;
  }

  get hasPhotos(): boolean { return this.capturedPhotos.length > 0; }
  get allPhotosTaken(): boolean { return this.capturedPhotos.length >= this.MAX_PHOTOS; }

  async ngAfterViewInit() {
    this.pageEnteredAt = Date.now();
    await this.modelSelection.initAsync(this.configDb, this.settings);
    if (this.viewDestroyed) return;
    this.cd.markForCheck();
  }

  // ── Camera ──────────────────────────────────────────────────────────

  onCameraStateChange() {
    this.cameraPermissionDismissed = this.cameraView?.cameraService.cameraPermissionDismissed ?? false;
    this.cd.markForCheck();
  }

  onCameraError(result: CameraInitResult) {
    if (result === CameraInitResult.NoCameraWorked) {
      if (this.cameraView?.cameraService.permissionStatus?.state === 'granted') {
        void this.showErrorAlertAsync(LL.labelScanner.cameraErrorTitle(), LL.labelScanner.error());
      }
    }
  }

  // ── Photo capture ─────────────────────────────────────────────────

  // haptic + shutter sound are handled by cs-scanner-shutter-bar
  async snapPhoto() {
    const photo = await this.cameraView.capturePhoto();

    const slotIndex = this.capturedPhotos.length;
    // replace (not mutate) the array — the OnPush reel/stage components
    // only re-render when the photos input gets a new reference
    this.capturedPhotos = [...this.capturedPhotos, photo];
    this.telemetry.trackEvent('label_scan_photo_taken', { photoIndex: slotIndex, source: 'camera' });

    this.resultGroups = null;
    this.activeGroupIndex = 0;
    this.scanId = null;
    this.cd.markForCheck();

    await animateCapturedPhotoToReelAsync(this.animationHooks, slotIndex, photo.dataUrl);
    if (this.viewDestroyed) return;

    if (this.allPhotosTaken) {
      await this.transitionToCollageAsync();
    }
  }

  // upload from the photo library via the shared scanner file picker
  // (owner decision #13: the label scanner gains the upload button the
  // other scanners already had)
  openFilePicker() {
    if (this.allPhotosTaken) return;
    openScannerImagePicker({
      safeTimeout: (callback, ms) => this.safeTimeout(callback, ms),
      // let the user pick several label photos at once, capped at the remaining slots
      maxFiles: this.MAX_PHOTOS - this.capturedPhotos.length,
      onPicked: async photo => {
        if (this.viewDestroyed || this.allPhotosTaken) return;

        const slotIndex = this.capturedPhotos.length;
        // replace (not mutate) — see snapPhoto
        this.capturedPhotos = [...this.capturedPhotos, photo];
        this.telemetry.trackEvent('label_scan_photo_taken', { photoIndex: slotIndex, source: 'upload' });

        this.resultGroups = null;
        this.activeGroupIndex = 0;
        this.scanId = null;
        this.cd.markForCheck();

        await animateCapturedPhotoToReelAsync(this.animationHooks, slotIndex, photo.dataUrl);
        if (this.viewDestroyed) return;

        if (this.allPhotosTaken) {
          await this.transitionToCollageAsync();
        }
      },
    });
  }

  removePhotoAt(index: number) {
    this.resultGroups = null;
    this.activeGroupIndex = 0;
    this.scanId = null;

    this.telemetry.trackEvent('label_scan_photo_removed', { photoIndex: index });
    // replace (not mutate) — see snapPhoto
    this.capturedPhotos = this.capturedPhotos.filter((_, i) => i !== index);
    this.cd.markForCheck();

    void this.cameraView.reinitCamera();
  }

  // discard the photos (and any comparison results) and start over with
  // the camera — used by the collage's and the comparison review's Retake
  // buttons. From the review the camera stage remounts when resultGroups
  // clears, and the fresh cs-camera-view starts the camera itself.
  retakeAllPhotos() {
    this.resultGroups = null;
    this.activeGroupIndex = 0;
    this.scanId = null;
    this.capturedPhotos = [];
    this.cd.markForCheck();
    void this.cameraView?.reinitCamera();
  }

  // tap a reel thumbnail to inspect the photo full-screen. On native the
  // camera preview composites outside the DOM (iOS cuts a mask hole in
  // the WebView, Android draws above it), so a DOM popover over the
  // camera rect is invisible — pause the camera while inspecting.
  async inspectPhotoAt(index: number) {
    const photo = this.capturedPhotos[index];
    if (!photo) return;

    const pauseCamera = !!this.cameraView?.isActive && this.cameraView.isNative;
    if (pauseCamera) {
      await this.cameraView.stopCamera();
      if (this.viewDestroyed) return;
      this.cd.markForCheck();
    }

    // resolves when the popover is dismissed
    await this.popoverCtrl.present(ZoomImagePopover, { imageSrc: photo.dataUrl, fullscreen: true }, {
      targetFromElement: this.reel?.slotElements[index],
      targetToElement: document.body,
      animate: true,
    });
    if (this.viewDestroyed) return;

    if (pauseCamera) {
      void this.cameraView?.reinitCamera();
    }
  }

  addPhoto() {
    if (this.allPhotosTaken) {
      this.removePhotoAt(this.capturedPhotos.length - 1);
      return;
    }
    void this.cameraView.reinitCamera();
  }

  // ── Model selection (for 'any' mode) ──────────────────────────────

  // the shared cs-scanner-model-picker emits the chosen key; persist it to the
  // one shared setting so the choice carries across every scanner (#3783)
  onModelSelected(key: string) {
    this.modelSelection.onModelSelected(key, this.settings);
    this.cd.markForCheck();
  }

  // ── Fake progress bar ────────────────────────────────────────────

  // per-tick sync from the shared fake-progress driver
  private onProgressTick() {
    if (this.viewDestroyed) {
      this.fakeProgress.stop();
      return;
    }
    this.cd.markForCheck();
  }

  private stopAnalyzing() {
    this.analyzing = false;
    this.fakeProgress.stop();
  }

  private getQuotaMessage(rejection: ScanQuotaRejection): TranslatedString {
    switch (rejection) {
      case ScanQuotaRejection.AccountLocked:
        return LL.labelScanner.quotaAccountLocked();
      case ScanQuotaRejection.RateLimited:
        return LL.labelScanner.quotaRateLimited();
      case ScanQuotaRejection.DailyCooldown:
        return LL.labelScanner.quotaDailyCooldown();
      case ScanQuotaRejection.QualityWarning:
        return LL.labelScanner.quotaQualityWarning();
      case ScanQuotaRejection.GlobalCap:
        return LL.common.messages.scanAppCapacityReached();
    }
  }

  // resetCamera: after the alert is dismissed, clear the captured photos and
  // restart the camera so an analyze error returns the user to a fresh camera
  // rather than leaving the failed photo on screen (owner decision 2026-06-15).
  private async showErrorAlertAsync(title: TranslatedString, message: TranslatedString, resetCamera = false) {
    this.errorAlertOpen = true;
    this.cd.markForCheck();
    await this.alertCtrl.showAlertAsync({ title, message });
    if (this.viewDestroyed) return;
    this.errorAlertOpen = false;
    this.cd.markForCheck();
    if (resetCamera) this.retakeAllPhotos();
  }

  // like showErrorAlertAsync, but for transient failures it adds a "Try Again"
  // button so the user can re-run the scan in place without re-photographing.
  // Returns 'retry' when the user taps Try Again, null otherwise (#3946).
  private async showScanErrorAsync(
    title: TranslatedString,
    message: TranslatedString,
    opts: { resetCamera?: boolean; offerRetry?: boolean } = {},
  ): Promise<'retry' | null> {
    // without a retry button this is just the plain OK-only alert — reuse
    // showErrorAlertAsync so the alert open/close/reset dance lives in one place
    if (!opts.offerRetry) {
      await this.showErrorAlertAsync(title, message, opts.resetCamera);
      return null;
    }

    // with a retry button the alert offers Try Again + Cancel (shared widget)
    this.errorAlertOpen = true;
    this.cd.markForCheck();
    const choice = await showScanRetryAlertAsync(this.alertCtrl, title, message);
    if (this.viewDestroyed) return null;
    this.errorAlertOpen = false;
    this.cd.markForCheck();
    if (opts.resetCamera) this.retakeAllPhotos();
    return choice === 'retry' ? 'retry' : null;
  }

  // resolve the title/message pair for a scan-error alert kind from the existing
  // labelScanner strings
  private scanErrorCopyFor(kind: ScanErrorAlertKind): { title: TranslatedString; message: TranslatedString } {
    switch (kind) {
      case 'offline':
        return { title: LL.labelScanner.offlineTitle(), message: LL.labelScanner.offlineMessage() };
      case 'incomplete':
        return { title: LL.labelScanner.incompleteTitle(), message: LL.labelScanner.incompleteMessage() };
      case 'provider':
        return { title: LL.labelScanner.providerErrorTitle(), message: LL.labelScanner.providerErrorMessage() };
    }
  }

  // central handler for a failed scan: pick the recovery policy, show the right
  // alert (with a Try Again button for transient failures), and re-run the scan
  // when the user taps it. The caller must have already stopped the progress bar
  // (and logged telemetry/Sentry where applicable — the offline pre-check
  // branches skip it since offline is an expected, not surfaced, condition) (#3946).
  private async handleScanFailureAsync(error: string | undefined) {
    await runScanFailureRecoveryAsync({
      error,
      isOnline: this.onlineState.isLikelyOnline(),
      // this scanner alone picks its title and message from the policy's copy class
      showScanErrorAsync: (alertOptions, policy) => {
        const { title, message } = this.scanErrorCopyFor(policy.kind);
        return this.showScanErrorAsync(title, message, alertOptions);
      },
      scheduleOnNextTask: run => this.safeTimeout(run, 0),
      retryScan: () => { if (!this.viewDestroyed) void this.analyzeLabel(); },
    });
  }

  // ── Analysis ──────────────────────────────────────────────────────

  // submit captured photos through the (single- or multi-model) label scan
  // pipeline and route the resulting nutrients through the result-grouping
  // UI. Holds the screen awake for the duration of the scan (release in
  // finally).
  async analyzeLabel() {
    if (!this.hasPhotos) return;

    // if camera is still active, stop it and animate to collage first
    if (this.cameraView?.isActive) {
      await this.transitionToCollageAsync();
      if (this.viewDestroyed) return;
    }

    this.analyzing = true;
    this.retryMessage = null;
    this.resultGroups = null;
    this.fakeProgress.start();
    this.cd.markForCheck();

    const startTime = Date.now();

    try {
      // hold screen awake across the (possibly multi-model) scan; released in
      // finally so every exit path — offline early-return, viewDestroyed,
      // success, error — drops the lock. Inside the try so finally is the
      // single release site.
      void this.wakeLock.acquireAsync('scanner:label');
      if (this.modelSelection.scanMode === ScanMode.Multi) {
        const result = await this.analyzeLabelMultiModelAsync();
        if (!result || this.viewDestroyed) return;
        // if all attempts failed due to offline, show offline alert (with retry)
        if (result.error && !this.onlineState.isLikelyOnline()) {
          this.stopAnalyzing();
          this.cd.markForCheck();
          await this.handleScanFailureAsync(result.error);
          return;
        }
        await this.handleScanResultAsync(result, Date.now() - startTime, result.modelKey || this.modelSelection.multiModels[0]);
      } else {
        const modelToSend = this.modelSelection.fixedModel;

        // if offline, show immediate feedback instead of waiting for timeout. This REFUSES the scan,
        // so it re-checks rather than trusting the cached state — a stale latched-Offline would
        // otherwise refuse every scan (and every retry, which re-reads the same value) on a device
        // that is working perfectly well.
        if (!await this.onlineState.isLikelyOnlineOrRecheckAsync()) {
          this.stopAnalyzing();
          this.cd.markForCheck();
          await this.handleScanFailureAsync(undefined);
          return;
        }

        this.telemetry.trackEvent('label_scan_start', {
          model: modelToSend,
          photoCount: this.capturedPhotos.length,
          timeToSubmitMs: Date.now() - this.pageEnteredAt,
        });

        try {
          const result: ScanLabelResults = await this.cloudFunctions.scanLabelAsync({
            imagesBase64: this.capturedPhotos.map(p => p.base64),
            model: modelToSend,
            // the server writes the food `description` in this language; the text it
            // reads off the packaging stays as printed (#4206)
            locale: BOOT_LOCALE,
          });
          if (this.viewDestroyed) return;
          await this.handleScanResultAsync(result, Date.now() - startTime, modelToSend);
        } catch (err) {
          const durationMs = Date.now() - startTime;
          if (this.viewDestroyed) return;
          console.error('Label analysis failed:', err);
          // if the cloud-functions layer already reported this (or left it unreported while offline),
          // it is a network drop: say so and file no second problem (#6664)
          const report = labelScanFailureReport(wasReportedByCloudFunctions(err), err?.message);
          this.telemetry.trackEvent('label_scan_error', { model: modelToSend, error: report.telemetryError, durationMs });
          if (report.fileProblem) this.logging.trackSilentProblem('Label scan failed', { model: modelToSend, error: err });
          this.stopAnalyzing();
          this.cd.markForCheck();
          // a thrown request error is transient (network drop or provider hiccup):
          // pass ProviderError as the stand-in; getScanErrorPolicy picks the
          // offline vs provider copy from the connectivity signal, keeps the photos, and
          // offers a Try Again button.
          await this.handleScanFailureAsync(ScanLabelError.ProviderError);
        }
      }
    } finally {
      void this.wakeLock.releaseAsync('scanner:label');
    }
  }

  private async handleScanResultAsync(result: LabelScanOutcome, durationMs: number, modelUsed: string) {
    // ── detailed client-side logging ──
    if (result.modelResults) {
      console.log(`Label scan ALL mode completed in ${durationMs}ms, scanId=${result.scanId}`);
      for (const [key, mr] of Object.entries(result.modelResults)) {
        console.log(`  ${key}: ${mr.error ? `ERROR: ${mr.error}` : `${Object.keys(mr.nutrients || {}).length} nutrients, ${mr.durationMs}ms, ${mr.inputTokens}in/${mr.outputTokens}out, confidence=${mr.confidence}, labelStyle=${mr.labelStyle}${mr.jsonRepaired ? ' [JSON REPAIRED]' : ''}`}`);
        if (mr.nutrients) console.log(`    nutrients:`, mr.nutrients);
        if (mr.measures) console.log(`    measures:`, mr.measures);
      }
    } else {
      console.log(`Label scan ${modelUsed} completed in ${durationMs}ms:`, {
        modelKey: result.modelKey,
        scanId: result.scanId,
        labelStyle: result.labelStyle,
        confidence: result.confidence,
        nutrients: result.nutrients,
        measures: result.measures,
        error: result.error,
      });
    }

    // ── 'all' mode: compare results ──
    if (result.modelResults) {
      this.scanId = result.scanId;
      await this.handleAllModeResults(result, durationMs);
      return;
    }

    // ── single / multi model mode ──

    // handle quota rejection
    if (result.quota?.rejection && result.quota.rejection !== ScanQuotaRejection.QualityWarning) {
      this.telemetry.trackEvent('label_scan_quota', { rejection: result.quota.rejection, scansToday: result.quota.scansToday });
      this.stopAnalyzing();
      this.cd.markForCheck();
      await this.showErrorAlertAsync(LL.labelScanner.quotaTitle(), this.getQuotaMessage(result.quota.rejection));
      return;
    }

    if (result.error) {
      // a request the network layer already reported is a network drop, not a provider failure (#6664)
      const report = labelScanFailureReport(!!result.handledByNetworkLayer, result.error);
      this.telemetry.trackEvent('label_scan_error', { model: modelUsed, error: report.telemetryError, durationMs });
      if (report.fileProblem) this.logging.trackSilentProblem('Label scan error', { model: modelUsed, error: result.error });
      this.stopAnalyzing();
      this.cd.markForCheck();
      // pick the recovery policy from the error: transient failures (provider
      // error, timeout, incomplete) keep the captured photos and offer a Try
      // Again button so the user can re-run the scan in place; genuinely
      // non-retryable errors reset to a fresh camera (#3946).
      await this.handleScanFailureAsync(result.error);
      return;
    }

    if (!result.nutrients || Object.keys(result.nutrients).length === 0) {
      this.telemetry.trackEvent('label_scan_no_nutrients', { model: modelUsed, reason: result.noNutrientsReason, durationMs });
      const message = result.noNutrientsReason === 'blurry'
        ? LL.labelScanner.noNutrientsBlurry()
        : LL.labelScanner.noNutrients();
      this.stopAnalyzing();
      this.cd.markForCheck();
      await this.showErrorAlertAsync(LL.labelScanner.noNutrientsTitle(), message, true);
      return;
    }

    if (hasPartialNutrients(result.nutrients)) {
      this.telemetry.trackEvent('label_scan_partial', { model: modelUsed, nutrientCount: Object.keys(result.nutrients).length, durationMs });
      this.stopAnalyzing();
      this.cd.markForCheck();
      await this.showErrorAlertAsync(LL.labelScanner.noNutrientsTitle(), LL.labelScanner.noNutrientsPartial(), true);
      return;
    }

    this.telemetry.trackEvent('label_scan_success', {
      model: modelUsed,
      nutrientCount: Object.keys(result.nutrients).length,
      labelStyle: result.labelStyle,
      confidence: result.confidence,
      durationMs,
      scansToday: result.quota?.scansToday,
      hasBarcode: !!result.barcode,
      hasIngredients: !!result.ingredients,
    });

    // show quality gate warning if present (scan still succeeded)
    if (result.quota?.rejection === ScanQuotaRejection.QualityWarning) {
      console.log(`Quota warning: quality gate (scans today: ${result.quota.scansToday})`);
    }

    // crop the representative image — stored in memory (too large for sessionStorage nav params)
    setPendingLabelScanImage(await this.cropRepresentativeImageAsync(
      result.representativeImageIndex, result.cropRect,
    ));

    await this.replaceWithEditFoodAsync({
      nutrients: result.nutrients,
      measures: result.measures,
      labelScanConfidence: result.confidence,
      labelScanNutrientConfidences: result.nutrientConfidences,
      labelScanJsonRepaired: result.jsonRepaired,
      foodName: result.foodName,
      brandName: result.brandName,
      ingredients: result.ingredients,
      description: result.description,
      labelStyle: result.labelStyle,
      barcode: result.barcode,
    });
  }

  /**
   * Hand the label's readings to the food editor, and with them everything this page was asked
   * to carry (#4968).
   *
   * Both ways out of this page — one result accepted straight off, or one group picked out of a
   * comparison — end in the identical navigation, so it is written once. A route that forgot to
   * carry the caller's return callback is the exact bug this ticket exists to fix: the food gets
   * made and saved, and the errand the user was on (adding it to a meal, logging it to a day)
   * silently ends there.
   *
   * `replaceTop` rather than push, so backing out of the food editor returns to whatever opened
   * this scanner rather than to a camera the user has finished with. The callback still arrives:
   * it is delivered by the food editor's own close, and its owner is found by name anywhere in
   * the page stack.
   */
  private async replaceWithEditFoodAsync(labelReadings: LabelReadings) {
    await this.navCtrl.replaceTopAsync(IEditFoodPage, {
      mode: 'createFood',
      // the whole errand, every part of it -- a part left behind ends it silently
      returnCallback: this.navParams.data.returnCallback,
      errandAnswerShape: this.navParams.data.errandAnswerShape,
      gotoLogFoodAfterSelect_dayDate: this.navParams.data.gotoLogFoodAfterSelect_dayDate,
      local: {
        ...labelReadings,
        // the barcode that failed to find anything, which is why the user is scanning a label
        scannedBarcode: this.navParams.data.ean,
        returnContext: this.navParams.data.local?.returnContext,
      },
    });
  }

  // ── Multi-model A/B with sequential fallback ──────────────────────

  private showRetryFeedback() {
    this.retryMessage = LL.labelScanner.retryingDifferentServer();
    // restart the bar from zero for the next model attempt
    this.fakeProgress.start();
    this.cd.markForCheck();
  }

  // shared blacklist + shuffle + retry loop; see multi-model-fallback.ts
  private async analyzeLabelMultiModelAsync(): Promise<LabelScanOutcome> {
    const images = this.capturedPhotos.map(p => p.base64);
    const tracker = new LabelScanRunTracker();
    const result = await runMultiModelScanAsync<LabelScanOutcome>({
      models: this.modelSelection.multiModels,
      manager: this.multiModelFallback,
      isOnline: () => this.onlineState.isLikelyOnlineOrRecheckAsync(),
      scanAsync: (modelKey, attempt, total) => {
        this.telemetry.trackEvent('label_scan_start', {
          model: modelKey,
          multi: true,
          attempt,
          photoCount: this.capturedPhotos.length,
          timeToSubmitMs: Date.now() - this.pageEnteredAt,
        });
        console.log(`Multi-model scan: trying ${modelKey} (attempt ${attempt + 1}/${total})`);
        return this.cloudFunctions.scanLabelAsync({ imagesBase64: images, model: modelKey, locale: BOOT_LOCALE });
      },
      isProviderSideError: result =>
        result.error === ScanLabelError.ProviderError || result.error === ScanLabelError.ProviderTimeout || result.error === ScanLabelError.IncompleteResponse,
      makeNetworkErrorResult: tracker.makeNetworkErrorResult,
      isAborted: () => this.viewDestroyed,
      onRetry: () => this.showRetryFeedback(),
      onNetworkError: (modelKey, err) =>
        console.log(`Multi-model scan: ${modelKey} network error:`, (err as Error)?.message),
      onProviderError: (modelKey, result) => {
        console.log(`Multi-model scan: ${modelKey} returned error ${result.error}, blacklisting for 1hr`);
        tracker.noteProviderError();
      },
    });
    return tracker.finish(result);
  }

  // ── 'all' mode result handling ────────────────────────────────────

  private async handleAllModeResults(result: ScanLabelResults, durationMs: number) {
    const modelResults = result.modelResults;
    const successfulResults: { key: string, result: ScanLabelModelResult }[] = [];
    const failedModels: string[] = [];

    for (const [key, mr] of Object.entries(modelResults)) {
      if (mr.error || !mr.nutrients || Object.keys(mr.nutrients).length === 0 || hasPartialNutrients(mr.nutrients)) {
        failedModels.push(key);
        console.log(`Label scan model ${key}: failed - ${mr.error || 'no nutrients or partial'}`);
      } else {
        console.log(`Label scan model ${key}: ${Object.keys(mr.nutrients).length} nutrients, ${mr.durationMs}ms, ${mr.inputTokens}in/${mr.outputTokens}out`);
        successfulResults.push({ key, result: mr });
      }
    }

    if (failedModels.length > 0) {
      this.telemetry.trackEvent('label_scan_all_failures', { failed: failedModels.join(',') });
      for (const key of failedModels) {
        this.logging.trackSilentProblem('Label scan model failed in all-mode', {
          model: key,
          error: modelResults[key].error,
          scanId: result.scanId,
        });
      }
    }

    if (successfulResults.length === 0) {
      const anyBlurry = Object.values(modelResults).some(mr => mr.noNutrientsReason === 'blurry');
      const anyPartial = Object.values(modelResults).some(mr => mr.nutrients && Object.keys(mr.nutrients).length > 0);
      this.telemetry.trackEvent('label_scan_all_no_results', { anyBlurry, anyPartial });
      const message = anyBlurry ? LL.labelScanner.noNutrientsBlurry()
        : anyPartial ? LL.labelScanner.noNutrientsPartial()
        : LL.labelScanner.noNutrients();
      this.stopAnalyzing();
      this.cd.markForCheck();
      await this.showErrorAlertAsync(LL.labelScanner.noNutrientsTitle(), message, true);
      return;
    }

    // group results by nutrient similarity
    this.resultGroups = groupResultsBySimilarity(successfulResults);

    this.telemetry.trackEvent('label_scan_all_complete', {
      totalModels: SCAN_MODELS.length,
      successModels: successfulResults.length,
      failedModels: failedModels.length,
      groupCount: this.resultGroups.length,
      durationMs,
    });

    // if all models agree, proceed directly
    if (this.resultGroups.length === 1) {
      console.log('All models agree — proceeding to edit-food');
      this.telemetry.trackEvent('label_scan_all_unanimous', {
        models: this.resultGroups[0].models.join(','),
      });

      // log the unanimous pick
      if (this.scanId) {
        this.cloudFunctions.logLabelScanChoiceAsync({
          scanId: this.scanId,
          pickedModel: this.resultGroups[0].models[0],
        }).catch(() => {});
      }

      this.stopAnalyzing();
      void this.navigateToEditFood(this.resultGroups[0]);
      return;
    }

    // if disagreement, show comparison UI
    console.log(`Models disagree — ${this.resultGroups.length} groups`);
    this.activeGroupIndex = 0;
    this.stopAnalyzing();
    this.cd.markForCheck();
  }

  // ── Comparison UI actions ─────────────────────────────────────────

  get stepperSteps(): StepConfig[] {
    if (!this.resultGroups) return [];
    return this.resultGroups.map((group, i) => ({
      label: `${group.models.length} model${group.models.length > 1 ? 's' : ''}`,
      state: i === this.activeGroupIndex ? StepState.Current : StepState.Pending,
    }));
  }

  onGroupStepClick(index: number) {
    this.activeGroupIndex = index;
    this.cd.markForCheck();
  }

  acceptGroup(group: ResultGroup) {
    this.telemetry.trackEvent('label_scan_group_accepted', {
      models: group.models.join(','),
      groupCount: this.resultGroups.length,
    });

    // log the user's pick
    if (this.scanId) {
      this.cloudFunctions.logLabelScanChoiceAsync({
        scanId: this.scanId,
        pickedModel: group.models[0],
      }).catch(() => {});
    }

    void this.navigateToEditFood(group);
  }

  private async navigateToEditFood(group: ResultGroup) {
    setPendingLabelScanImage(await this.cropRepresentativeImageAsync(
      group.representativeImageIndex, group.cropRect,
    ));

    await this.replaceWithEditFoodAsync({
      nutrients: group.nutrients,
      measures: group.measures,
      labelScanConfidence: Math.max(...group.confidences.map(c => c.confidence || 0)),
      labelScanNutrientConfidences: group.nutrientConfidences,
      labelScanJsonRepaired: group.jsonRepaired,
      foodName: group.foodName,
      brandName: group.brandName,
      ingredients: group.ingredients,
      description: group.description,
      labelStyle: group.labelStyle,
      barcode: group.barcode,
    });
  }

  get totalModelsInComparison(): number {
    if (!this.resultGroups) return 0;
    return this.resultGroups.reduce((sum, g) => sum + g.models.length, 0);
  }

  // A confidence fraction (0..1) as a whole-percent label, localized. Replaces an
  // Angular `| number` pipe so the number follows the user's locale, not LOCALE_ID. #4204
  confidencePctLabel(fraction: number | null | undefined): string {
    if (fraction == null || isNaN(fraction)) return '?';
    return formatLocaleFloat(Math.round(fraction * 100), this.settings.locale);
  }

  // A nutrient amount to one decimal place, localized decimal separator. #4204
  nutrientValueLabel(value: number): string {
    if (value == null || isNaN(value)) return '';
    return formatLocaleFloat(Number(value.toFixed(1)), this.settings.locale, true);
  }

  getNutrientEntries(nutrients: Nutrients): { key: string, label: string, value: number, agreeCount: number, confidence: number }[] {
    if (!this.resultGroups) return [];

    // the value, the agreement count and the confidence come from the comparison
    // module; this method only adds the translated name. That label comes from
    // nutrientName() rather than a module-level English map, so it follows the
    // user's language -- and looking it up here rather than at module load
    // avoids reading the translation catalog before it exists (#4368)
    return nutrientAgreements(this.resultGroups, nutrients)
      .map(entry => ({ ...entry, label: nutrientName(entry.key) }));
  }

  // ── Representative image cropping ────────────────────────────────

  private async cropRepresentativeImageAsync(
    imageIndex?: number,
    cropRect?: { x: number; y: number; w: number; h: number },
  ): Promise<string | undefined> {
    console.log('cropRepresentativeImage:', { imageIndex, cropRect, photosCount: this.capturedPhotos.length });
    const photo = this.capturedPhotos[imageIndex ?? 0];
    if (!photo) { console.log('cropRepresentativeImage: no photo at index', imageIndex ?? 0); return undefined; }

    try {
      return cropRect
        ? await this.cropImageAsync(photo.dataUrl, cropRect)
        : photo.dataUrl;
    } catch (err) {
      // the food is then saved without its picture, which the person may not notice
      this.logging.trackSilentProblem('LabelScanImageCropFailed', { detail: describeError(err) });
      return undefined;
    }
  }

  private cropImageAsync(
    dataUrl: string,
    rect: { x: number; y: number; w: number; h: number },
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // a thin crop is grown so the upload can make its sizes from it (df-376e65c95f28)
        const { sx, sy, sw, sh } = labelScanCropPixels(rect, img.width, img.height);

        const canvas = document.createElement('canvas');
        canvas.width = sw;
        canvas.height = sh;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => reject(new Error('Failed to load image for cropping'));
      img.src = dataUrl;
    });
  }

  // ── Animations (shared helpers in scanner-capture-animations.ts) ──

  // this scanner is the only one that does not wait for the camera teardown
  // before switching to the collage — see CameraStopOrdering, and the
  // "Decision: no ScannerPageBase" section of
  // design-docs/scanner-shared-components.md, which records it as deliberate
  private async transitionToCollageAsync() {
    await animateCollageTransitionAsync(this.animationHooks, {
      isCameraActive: !!this.cameraView?.isActive,
      stopCameraAsync: async () => { await this.cameraView?.stopCamera(); },
      cameraStopOrdering: CameraStopOrdering.AlongsideCollage,
    });
  }

  /** cs-nav awaits ionViewCanLeave before starting the page transition;
   *  fire-and-forgets ionViewWillLeave. Await the native camera teardown
   *  here — the AVCapturePreviewLayer would otherwise stay visible on
   *  top of the incoming page during the route transition. */
  async ionViewCanLeave() {
    await this.cameraView?.stopCamera();
    return true;
  }

  async ionViewWillLeave() {
    this.fakeProgress.stop();
  }

  ngOnDestroy() {
    void this.cameraView?.stopCamera();
    this.fakeProgress.stop();
  }
}
